import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const STORAGE_KEY = 'heliophant-cookie-consent'; // value: 'accepted' | 'rejected' | unset
// Note: only stores a non-sensitive preference ('accepted' | 'rejected').
// No PII or auth tokens are ever persisted here. Auth uses httpOnly cookies.

const T = {
  en: {
    title: 'We value your privacy',
    body: 'We use essential cookies to run the site and optional cookies to understand how you use it. You can accept all, reject non-essential cookies, or read more in our Privacy Policy.',
    accept: 'Accept all',
    reject: 'Reject non-essential',
    privacy: 'Privacy Policy',
  },
  el: {
    title: 'Σεβόμαστε την ιδιωτικότητά σας',
    body: 'Χρησιμοποιούμε απαραίτητα cookies για τη λειτουργία του site και προαιρετικά για ανάλυση χρήσης. Μπορείτε να αποδεχτείτε όλα, να απορρίψετε τα μη-απαραίτητα, ή να δείτε περισσότερα στην Πολιτική Απορρήτου.',
    accept: 'Αποδοχή όλων',
    reject: 'Απόρριψη μη-απαραίτητων',
    privacy: 'Πολιτική Απορρήτου',
  },
};

const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (!existing) setVisible(true);
    // Allow other components (e.g. a "Cookies" link in the footer) to reopen
    // the banner without forcing a full page reload.
    const reopen = () => setVisible(true);
    window.addEventListener('heliophant:reopen-cookie-consent', reopen);
    return () => window.removeEventListener('heliophant:reopen-cookie-consent', reopen);
  }, []);

  const persist = useCallback((decision) => {
    localStorage.setItem(STORAGE_KEY, decision);
    localStorage.setItem(`${STORAGE_KEY}-at`, new Date().toISOString());
    setVisible(false);
    // Fire same-tab event so AnalyticsLoader (or other consent-aware
    // components) can react without waiting for a full reload.
    window.dispatchEvent(new CustomEvent('heliophant:cookie-consent-changed', { detail: { decision } }));
  }, []);

  if (!visible) return null;

  const t = T[language] || T.en;
  const privacyHref = language === 'el' ? '/privacy-gr' : '/privacy';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-[60] max-w-3xl md:mx-auto"
    >
      <div className="bg-gradient-to-br from-[#0a0e27]/95 to-[#050811]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/40 p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-semibold text-base mb-1">{t.title}</h2>
            <p className="text-white/70 text-sm leading-relaxed">
              {t.body}{' '}
              <Link
                to={privacyHref}
                className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                data-testid="cookie-consent-privacy-link"
              >
                {t.privacy}
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
            <button
              onClick={() => persist('rejected')}
              data-testid="cookie-consent-reject"
              className="px-4 py-2 rounded-lg border border-white/20 text-white/90 text-sm hover:bg-white/5 transition-colors whitespace-nowrap"
            >
              {t.reject}
            </button>
            <button
              onClick={() => persist('accepted')}
              data-testid="cookie-consent-accept"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-sm font-medium whitespace-nowrap transition-colors"
            >
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
